import { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import { ProductMenu } from './ProductMenu';
import { TopNav } from './TopNav';
import { UserMenu } from './UserMenu';
import { usePalette } from './PaletteProvider';
import { SearchLineIcon } from './barIcons';

/**
 * The bar across the top of the portal (DX-275), floating since DX-352.
 *
 * This is the element every other revenexx surface has and Backstage did not:
 * the public Developer Portal, Atlas and the Cockpit all put a dark bar across
 * the top and keep the navigation column light. An earlier pass here inverted
 * that — dark sidebar, no top bar — which used the right palette in the wrong
 * arrangement, and side by side the portals still did not match.
 *
 * What it carries is what those three agree on: the logo and a product
 * switcher on the left, and search, appearance and the signed-in person on the
 * right.
 *
 * Between them sit the portal's sections (`TopNav`, DX-74). This bar was built
 * without them, on the argument that Backstage has more top-level entries than
 * the Developer Portal's seven and that a list that long is a sidebar's job.
 * That argument was about the wrong number: the entries are many, but the
 * SECTIONS are four, and the other portals put exactly that row up here. The
 * sidebar keeps the full list — the bar says which section you are in, the
 * column says where you are inside it.
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  /*
    The bar floats (DX-352): inset from the window's top and sides, rounded,
    with a shadow — the public portals' `fixed top-4 left-4 right-4
    lg:left-8 lg:right-8`, and inside it a bar no wider than the frame below,
    so their edges line up. Fixed rather than sticky, because the page under it
    no longer scrolls; the frame's content does.
  */
  dock: {
    position: 'fixed',
    top: 'var(--ui-shell-inset)',
    left: 'var(--ui-shell-gutter)',
    right: 'var(--ui-shell-gutter)',
    zIndex: theme.zIndex.appBar + 1,
  },
  bar: {
    position: 'relative',
    maxWidth: 'var(--ui-shell-max)',
    margin: '0 auto',
    height: 'var(--ui-topbar-height)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: theme.spacing(0, 2),
    /*
      The bar must be allowed to shrink, or its contents push the page wider
      than the window and everything scrolls sideways. `min-width: 0` is what
      lets the flex children give way — NOT `overflow: hidden`, which also
      clips the product menu that hangs out of the bar below it. That mistake
      left the menu as a thin white strip the height of the bar.
    */
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
      padding: theme.spacing(0, 1),
    },
    // Near-black on light, brand violet on dark — the portals' bar in each.
    borderRadius: 'var(--ui-bar-radius)',
    border: '1px solid var(--ui-bar-border)',
    boxShadow: 'var(--ui-bar-shadow)',
    boxSizing: 'border-box',
    /*
      The colour and the blur sit on a layer BEHIND the bar's contents rather
      than on the bar itself. A `backdrop-filter` on the bar would make it the
      box every `position: fixed` element inside it is placed in — and the
      command palette is one, so it would open inside the bar, 56px tall,
      instead of over the page.
    */
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      background: 'var(--ui-bar-bg)',
      backdropFilter: 'blur(8px)',
    },
    isolation: 'isolate',
  },
  /*
    Three columns, as on the Developer Portal: the two outer ones are
    `flex: 1 1 0`, so they always take an equal share of what is left and the
    sections between them sit in the middle of the bar however wide either
    side is (DX-352). `min-width: 0` lets either give way on a narrow window
    instead of pushing the bar wider than it.
  */
  left: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  right: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  /**
   * The search trigger. It opens the command palette, which is the portal's
   * one search (DX-341) — the same window ⌘K opens, so the shortcut printed on
   * the button is the truth about it.
   *
   * An icon and the shortcut, not a field: the Developer Portal's trigger is
   * a pill of that size, and a box in the bar read as a form to fill in
   * rather than a door to open (DX-352).
   */
  search: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 32,
    padding: '0 12px',
    border: 'none',
    borderRadius: 'var(--radius-pill)',
    background: 'none',
    color: 'var(--ui-bar-ink)',
    font: 'inherit',
    cursor: 'pointer',
    transition: 'background 0.15s ease, color 0.15s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.10)',
      color: '#FFFFFF',
    },
  },
  kbd: {
    padding: '2px 6px',
    borderRadius: 4,
    background: 'rgba(255, 255, 255, 0.05)',
    fontSize: 10,
    lineHeight: '14px',
    fontFamily: 'var(--font-mono)',
    opacity: 0.6,
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
}));

export const TopBar = () => {
  const classes = useStyles();

  /*
    One search, opened by ⌘K and by the button alike (DX-341).

    Until DX-341 these were two windows: ⌘K opened the palette, and the button
    — which printed ⌘K on itself — opened Backstage's search modal over the
    index. The split was argued on speed, that the palette must not wait on a
    full-text index to answer "take me to ai-gateway"; but the palette asks the
    backend too, and the index now answers in a section of its own below the
    things, so nothing above it waits. What the split did cost was a reader
    guessing which of two lookalike doors held the answer, and the button
    sending everyone who read its label to the other one.
  */
  const palette = usePalette();

  // ⌘K / Ctrl-K, the shortcut the other revenexx portals use and advertise on
  // the button itself — a label promising a shortcut that does nothing is
  // worse than no label.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k') {
        return;
      }
      // Not while someone is typing somewhere else: ⌘K is a text shortcut in
      // several editors, and swallowing it inside a field is worse than not
      // offering it. The palette's own input is covered by the same rule — it
      // is already open when the caret is in there.
      const el = e.target as HTMLElement | null;
      // Inside the palette the shortcut closes it again, which is what a
      // toggle means and what every other ⌘K does. Without this exception the
      // rule below swallows it — the palette focuses its own input on open, so
      // the second press always lands on an INPUT and returned early, leaving
      // a toggle that could only be turned on. It did not preventDefault
      // either, so the browser's own ⌘K was free to fire on top.
      const inPalette = !!el?.closest('[aria-label="Command palette"]');
      if (
        !inPalette &&
        (el?.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(el?.tagName ?? ''))
      ) {
        return;
      }
      e.preventDefault();
      palette.toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [palette]);

  return (
    <div className={classes.dock}>
      <header className={classes.bar}>
        <div className={classes.left}>
          <ProductMenu />
        </div>

        {/* The sections, centred between the product switcher and the
          actions — the arrangement the public Developer Portal uses. */}
        <TopNav />

        <div className={classes.right}>
          {/* The palette, opened from here and from ⌘K — and drawn by
              PaletteProvider, outside the bar. Named in the portal's
              language: the label read "Suchen" in an English interface. */}
          <button
            type="button"
            className={classes.search}
            onClick={() => palette.open()}
            aria-label="Search"
          >
            <SearchLineIcon />
            <kbd className={classes.kbd}>⌘K</kbd>
          </button>

          <UserMenu />
        </div>
      </header>
    </div>
  );
};
